/**
 * Layout sidebar — collapsible "Services" tree node.
 *
 * Extracted from `Layout.jsx` (no behaviour change). Wraps
 * `<OperationsCasesTree>` so its full content (All Cases + service rows
 * + sub-types) appears only when the user expands it. State is
 * persisted in localStorage and auto-expands when the URL carries a
 * taxonomy filter (`service_id`, `service_sub_type_id`,
 * `service_category_id`).
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import OperationsCasesTree from "@/components/sidebar/OperationsCasesTree";

export default function ServicesTreeNode({ item, pathname, onNavigate }) {
  const STORAGE_KEY = "nav.services-tree.open";
  const location = useLocation();
  const hasTaxonomy = useMemo(() => {
    if (!location.search) return false;
    const sp = new URLSearchParams(location.search);
    return !!(sp.get("service_id") || sp.get("service_sub_type_id") || sp.get("service_category_id"));
  }, [location.search]);

  const persisted = typeof window !== "undefined"
    ? window.localStorage.getItem(STORAGE_KEY) : null;
  const [open, setOpen] = useState(
    persisted == null ? (pathname.startsWith("/cases") || hasTaxonomy) : persisted === "1"
  );

  // Auto-expand when the URL gains a taxonomy filter (drilldown, deep link).
  useEffect(() => {
    if (hasTaxonomy && !open) setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasTaxonomy]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { window.localStorage.setItem(STORAGE_KEY, next ? "1" : "0"); } catch (e) { /* ignore */ }
  };

  const Icon = item.icon;
  const branchActive = pathname.startsWith("/cases");
  return (
    <div data-testid={item.testid} className="space-y-0.5">
      <div data-testid="sidebar-root-services">
        <button type="button" onClick={toggle}
                aria-expanded={open}
                title={item.label}
                data-testid="nav-services-toggle"
                className={`co-sidebar-link co-sidebar-child w-full justify-between ${branchActive ? "active" : ""}`}>
          <span className="flex items-center gap-2 min-w-0">
            <Icon className="w-4 h-4 shrink-0" />
            <span className="truncate">{item.label}</span>
          </span>
          <ChevronRight className={`w-3 h-3 transition-transform shrink-0 ${open ? "rotate-90" : ""}`} />
        </button>
      </div>
      {open ? (
        <div className="ml-2 pl-1 border-l border-slate-700/60"
             data-testid="nav-services-children">
          <OperationsCasesTree onNavigate={onNavigate} />
        </div>
      ) : null}
    </div>
  );
}
